import type { EngineStatus } from "@gf/shared";
import { igOperationLabel } from "@gf/shared/ig-operations";
import { Badge, Tooltip } from "@gf/ui";
import { engineState, formatDateTime, relativeTime } from "../lib/format";

/** Running / Paused until … / Idle / Not reporting, from IgAccount.engineStatus. */
export function EngineBadge({ status, size = "md" }: { status: EngineStatus | null | undefined; size?: "sm" | "md" }) {
  const st = engineState(status);
  const pauses = status?.operationPauses?.filter(p => p.until === null || p.until > Date.now()) ?? [];
  if (st === "running" && pauses.length) return <Tooltip content={pauses.map(p => `${igOperationLabel(p.operation)}: ${p.reason}${p.until ? ` · retries after ${formatDateTime(p.until)}` : ""}`).join("; ")}><Badge tone="warn" size={size} tabIndex={0}>Running · {pauses.length} operation{pauses.length === 1 ? "" : "s"} waiting</Badge></Tooltip>;
  if (st === "running")
    return (
      <Badge tone="success" dot size={size}>
        Automations running
      </Badge>
    );
  if (st === "paused") {
    const badge = (
      <Badge tone="warn" dot size={size} tabIndex={0}>
        {status?.pausedUntil ? `Paused until ${formatDateTime(status.pausedUntil)}` : "Account access needs attention"}
      </Badge>
    );
    return status?.pauseReason ? <Tooltip content={status.pauseReason}>{badge}</Tooltip> : badge;
  }
  if (st === "idle")
    return (
      <Badge tone="neutral" dot size={size}>
        Idle
      </Badge>
    );
  return (
    <Badge tone="neutral" size={size}>
      Not reporting yet
    </Badge>
  );
}

/** One line of plain English about the engine, for cards. */
export function engineSentence(status: EngineStatus | null | undefined): string {
  const st = engineState(status);
  if (st === "running") {
    const waits = status?.operationPauses?.filter(p => p.until === null || p.until > Date.now()) ?? [];
    return `The extension checked in ${relativeTime(status?.updatedAt)} and is working through your automations.${waits.length ? ` Waiting: ${waits.map(p => `${igOperationLabel(p.operation)}${p.until ? ` until ${formatDateTime(p.until)}` : ""}`).join("; ")}. Other operations continue.` : ""}`;
  }
  if (st === "paused")
    return `${status?.pausedUntil ? `Automation is paused until ${formatDateTime(status.pausedUntil)}.` : "Account access needs attention. Open Instagram, resolve the sign-in or security check, then click Resume in the extension."}${status?.pauseReason ? ` Reason: ${status.pauseReason}.` : ""}`;
  if (st === "idle") return `The extension checked in ${relativeTime(status?.updatedAt)}. Nothing is running right now.`;
  return "The extension hasn't reported on this account yet. Keep Instagram open in a tab with the extension installed.";
}
